/**
 * Patient actions during ACT (DESIGN §6.5): tests, Stabilise, routes, treatment,
 * Theatre pairing, ICU Support and step-down, discharge, early discharge, Locum.
 *
 * Each handler has a `check` over PublicState (no access to backs) and a `run`
 * that resolves with the full state.
 */
import type { SeatCommand } from '../commands.ts';
import { diag, EXPLORATORY_POLISHING } from '../content/compendium.ts';
import { testKindFor, buildSpecial } from '../deck.ts';
import type { Booking, Patient, PatientId, Phase, PublicState, Role, Route, Step, TableState, TestKind, TestResult } from '../types.ts';
import { CONFIRMED } from '../types.ts';
import {
  boxUp,
  canPerform,
  diagName,
  effectiveRoles,
  emit,
  eventActive,
  freeBedIndex,
  hasAccess,
  nextStep,
  onlyObservesRemain,
  patient,
  place,
  routeOf,
  seatOfBed,
  type Ctx,
} from './common.ts';
import { applyRiskIfWrong, attend, die, deteriorate, flip, normaliseRoute, release, reveal } from './outcomes.ts';

// ───────────────────────────── handler plumbing ─────────────────────────────

export interface SeatHandler<C extends SeatCommand = SeatCommand> {
  phases: Phase[];
  /** Action cost (0 = free). */
  cost(pub: PublicState, t: TableState, role: Role, c: C): number;
  /** The patient this command acts on. */
  target(pub: PublicState, t: TableState, role: Role, c: C): PatientId | null;
  /** Return a player-facing reason, or null when legal. PublicState only. */
  check(pub: PublicState, t: TableState, role: Role, c: C): string | null;
  run(ctx: Ctx, t: TableState, role: Role, c: C): void;
}

type H<T extends SeatCommand['type']> = SeatHandler<Extract<SeatCommand, { type: T }>>;

const ZERO = () => 0;
const ONE = () => 1;
const NO_TARGET = () => null;
const P_TARGET = (_pub: PublicState, _t: TableState, _r: Role, c: { p: string }) => c.p;

/** Spend actions and record bookkeeping common to every costed action. */
export function spend(ctx: Ctx, t: TableState, role: Role, cost: number, target: PatientId | null, kind: string): void {
  const st = t.seats[role]!;
  if (cost > 0) {
    st.actions.spent += cost;
    st.actions.available -= cost;
    const turn = ctx.s.clock.turn;
    st.stats.actionsByTurn[turn] = (st.stats.actionsByTurn[turn] ?? 0) + cost;
  }
  if (target) {
    st.lastActedOn = target;
    const p = ctx.s.patients[target];
    if (p) {
      attend(ctx, p, role);
      if (p.seat && p.seat !== role && cost > 0) {
        st.stats.helpGiven++;
        const holder = t.seats[p.seat];
        if (holder) holder.stats.helpReceived++;
      }
    }
  }
  emit(ctx, { type: 'ACTION_SPENT', table: t.no, seat: role, patient: target, text: `${role} ${kind}`, data: { kind, cost } });
}

function accessReason(pub: PublicState, t: TableState, role: Role, id: string): string | null {
  const p = pub.patients[id];
  if (!p || p.table !== t.no || p.status !== 'ACTIVE') return 'That patient is not on your table.';
  if (!hasAccess(pub, t, role, p)) return 'That patient is not on your list. Ask the holder to open the card to you.';
  return null;
}

function commsDown(pub: PublicState): boolean {
  return eventActive(pub, 'E26');
}

// ───────────────────────────── reading lines ─────────────────────────────

/** Resolve the issued line using its frozen working diagnosis. */
function applyTestResult(ctx: Ctx, t: TableState, p: Patient, test: TableState['tests'][number]): TestResult {
  const back = ctx.s.backs[p.id]!;
  const r: TestResult = test.diagnosis !== p.provisionalKey ? { kind: 'CONFIRMED' } : (back.results[test.test] ?? { kind: 'INCONCLUSIVE' });
  const text = lineText(r, test.diagnosis);
  const holder = p.seat ?? p.flags.lastSeat;
  reveal(ctx, p, test.test, text);
  emit(ctx, {
    type: 'LINE_READ', table: t.no, seat: holder, patient: p.id, text: `${p.name}: ${text}`,
    audience: ['faculty', ...(holder ? ([`seat:${holder}`] as const) : []), ...(test.by !== 'LOCUM' && test.by !== holder ? ([`seat:${test.by}`] as const) : [])],
    data: { test: test.test, result: r.kind },
  });
  if (p.status !== 'ACTIVE' || p.believedKey !== test.diagnosis) return r;
  if (p.flags.cluster) {
    p.box = p.flags.clusterTrueBox ?? p.box;
    p.flags.cluster = false;
  }
  switch (r.kind) {
    case 'RULES_OUT': {
      if (!p.ruledOut.includes(r.x)) p.ruledOut.push(r.x);
      const diffs = diag(p.believedKey).differentials;
      if (diffs.every((d) => p.ruledOut.includes(d))) p.box = CONFIRMED;
      else if (p.box !== CONFIRMED) p.box = boxUp(p.box);
      break;
    }
    case 'CONFIRMED': p.box = CONFIRMED; break;
    case 'INCONCLUSIVE': break;
    case 'FLIP': flip(ctx, t, p, r.x, test.test.toLowerCase().replace('_', ' ')); break;
  }
  return r;
}

/** Apply one queued result without rewriting later clinical consequences. */
function resolveDiagnostic(ctx: Ctx, t: TableState, test: TableState['tests'][number]): void {
  const p = patient(ctx.s, test.patient);
  const result = applyTestResult(ctx, t, p, test);
  const step = test.routeStep;
  if (!step || result.kind === 'FLIP' || p.status !== 'ACTIVE') return;
  if (p.route?.diag !== step.diag || p.route?.code !== step.code || p.route?.next !== step.next) return;
  completeStep(ctx, t, p, test.by, nextStep(p)?.label ?? 'Scan');
}

/** Release diagnostic results after all frozen care and ordinary ticking. */
export function resolveDiagnostics(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    const tests = t.tests;
    t.tests = [];
    for (const test of tests) resolveDiagnostic(ctx, t, test);
  }
}

export function lineText(r: TestResult, believed: string): string {
  switch (r.kind) {
    case 'RULES_OUT':
      return `Rules out ${diagName(r.x)}.`;
    case 'CONFIRMED':
      return `${diagName(believed).toUpperCase()} — confirmed.`;
    case 'INCONCLUSIVE':
      return 'Inconclusive.';
    case 'FLIP':
      return `It's ${diagName(r.x)} — flip.`;
  }
}

/** Queue an issued investigation without revealing its result to frozen care. */
export function readLine(ctx: Ctx, t: TableState, p: Patient, test: TestKind, by: Role | 'LOCUM', routeStep: TableState['tests'][number]['routeStep'] = null): void {
  t.tests.push({ patient: p.id, test, by, diagnosis: p.believedKey, routeStep });
  emit(ctx, { type: 'TEST_ORDERED', table: t.no, seat: p.seat, patient: p.id, text: `${p.name}: ${test.toLowerCase().replace('_', ' ')} result pending.`, audience: ['faculty', ...(p.seat ? ([`seat:${p.seat}`] as const) : [])] });
}

/** Scanner box available to this seat? Returns the box index or -1. */
export function scannerBox(pub: PublicState, t: TableState, role: Role): number {
  if (eventActive(pub, 'E4') || eventActive(pub, 'E9') || eventActive(pub, 'E42')) return -1;
  const cap = eventActive(pub, 'E1') ? 1 : 2;
  for (let i = 0; i < cap; i++) {
    if (t.board.scannerUsedBy[i]) continue;
    const alloc = t.board.allocations.scanner[i] ?? null;
    if (alloc === null || alloc === role) return i;
  }
  return -1;
}

function useScanner(ctx: Ctx, t: TableState, role: Role): void {
  const i = scannerBox(ctx.s, t, role);
  t.board.scannerUsedBy[i] = role;
  t.board.scannerUsed++;
  t.dashboard.scans++;
  t.tableStats.scansTotal++;
}

function opinionKind(role: Role): TestKind | null {
  return role === 'PHYS' ? 'OPINION_PHYS' : role === 'PSYCH' ? 'OPINION_PSYCH' : role === 'SURG' ? 'OPINION_SURG' : null;
}

/** Which specialties' opinions does the Compendium name for this patient? */
export function namedOpinions(t: TableState, p: Patient): TestKind[] {
  const d = diag(p.believedKey);
  const kinds = new Set<TestKind>();
  if (d.definitive) kinds.add(testKindFor(d.definitive, t.fiveSeat));
  for (const k of Object.values(d.ruleOuts)) kinds.add(testKindFor(k, t.fiveSeat));
  return [...kinds].filter((k) => k.startsWith('OPINION'));
}

// ───────────────────────────── treatment resolution ─────────────────────────────

/** Does acting on this patient require reading the TRUTH line? (box not ✓, or a Barnacle not yet found) */
function needsTruthRead(ctx: Ctx, p: Patient): boolean {
  const back = ctx.s.backs[p.id]!;
  if (p.box !== CONFIRMED) return true;
  return back.barnacle && p.believedKey !== back.truthKey;
}

/**
 * Resolve the next step of the route as done by `by`. Handles the TRUTH read,
 * risk if wrong, route advance and ⚠ bookkeeping. Returns false if the step
 * did not complete (wrong diagnosis).
 */
export function completeStep(ctx: Ctx, t: TableState, p: Patient, by: Role | 'LOCUM', what: string): boolean {
  const route = routeOf(p);
  const st = nextStep(p, route);
  if (!route || !st || !p.route) return false;
  const back = ctx.s.backs[p.id]!;
  const actor = by === 'LOCUM' ? null : t.seats[by];
  if (actor && !p.flags.treated) {
    if (p.box === CONFIRMED) actor.stats.confirmedBeforeTreat++;
  }
  p.flags.treated = true;
  p.referral = null;
  if (needsTruthRead(ctx, p)) {
    const unconfirmed = p.box !== CONFIRMED;
    if (actor && unconfirmed) actor.stats.unconfirmedTreats++;
    if (back.truthKey === p.believedKey) {
      p.box = CONFIRMED;
      reveal(ctx, p, 'TRUTH', `TRUTH: ${diagName(back.truthKey)}`);
      emit(ctx, { type: 'TREAT_UNCONFIRMED', table: t.no, seat: p.seat, patient: p.id, severity: 1, tag: 'Probability × risk', text: `${p.name} treated on the provisional: it was right.`, data: { matched: true, risk: route.riskIfWrong } });
    } else {
      if (actor && (route.riskIfWrong === 'HIGH' || route.riskIfWrong === 'CRITICAL')) actor.stats.unconfirmedWrongHighCrit++;
      emit(ctx, { type: 'TREAT_UNCONFIRMED', table: t.no, seat: p.seat, patient: p.id, severity: 2, tag: 'Probability × risk', text: `${p.name} treated on the provisional: it was wrong (${route.riskIfWrong}).`, data: { matched: false, risk: route.riskIfWrong } });
      applyRiskIfWrong(ctx, t, p, back.truthKey, route.riskIfWrong, what);
      return false;
    }
  }
  if (st.blood && t.board.blood) {
    t.board.blood.units -= st.blood;
    t.board.blood.used += st.blood;
    t.dashboard.bloodUsed += st.blood;
  }
  p.route.next++;
  p.turnScratch.stepsDone++;
  if (st.warn) {
    p.turnScratch.warnRoute = route.code;
    p.turnScratch.warnBlood = route.steps.some((x) => (x.blood ?? 0) > 0);
  }
  normaliseRoute(p);
  emit(ctx, { type: 'STEP_DONE', table: t.no, seat: p.seat, patient: p.id, text: `${p.name}: ${st.label} done.`, audience: ['faculty', ...(p.seat ? ([`seat:${p.seat}`] as const) : [])] });
  if (st.kind === 'THEATRE' && /Caesarean/.test(st.label)) {
    const nb = buildSpecial(ctx.s, ctx.rng, t.no, t.fiveSeat, 'newborn', { name: `Baby ${p.name.split(' ').slice(-1)[0]}` });
    const baby = ctx.s.patients[nb]!;
    baby.status = 'ACTIVE';
    baby.arrivedTurn = ctx.s.clock.turn;
    if (t.seats.SURG) place(ctx.s, t, 'SURG', baby, 'auto');
    emit(ctx, { type: 'NEWBORN', table: t.no, seat: 'SURG', patient: nb, severity: 1, text: `A Brand New Person has arrived on the surgical ward.`, audience: ['faculty', 'seat:SURG'] });
  }
  return true;
}

function stepOwnersOk(st: Step, roles: Role[]): boolean {
  return canPerform(st, roles);
}

// ───────────────────────────── Theatre ─────────────────────────────

export function theatreClosed(pub: PublicState): string | null {
  if (eventActive(pub, 'E3')) return 'The Theatre is double-booked this turn.';
  if (eventActive(pub, 'E9')) return 'Fire alarm: no Theatre this turn.';
  if (eventActive(pub, 'E26')) return 'Comms are down: nobody can coordinate the Theatre this turn.';
  return null;
}

export function ventsFree(pub: PublicState, t: TableState): number {
  const v = t.board.vents;
  if (!v) return 99;
  const recalled = v.recalledUntil >= pub.clock.turn ? v.recalled : 0;
  const held = Object.values(pub.patients).filter((p) => p.table === t.no && (p.status === 'ACTIVE' || p.status === 'INCOMING') && p.flags.ventHeld).length;
  return v.total - recalled - held - (t.board.theatre.ventTaken ? 1 : 0) - (t.board.secondTheatre?.ventTaken ? 1 : 0);
}

function completeTheatre(ctx: Ctx, t: TableState, p: Patient, theatre: 0 | 1 = 0): void {
  const th = theatre === 1 ? t.board.secondTheatre! : t.board.theatre;
  th.used = true;
  if (theatre === 0) t.board.theatre.booking = null;
  else t.bookings.find((b) => b.kind === 'THEATRE')!.used++;
  if (t.board.vents) th.ventTaken = true;
  t.dashboard.theatre++;
  t.seats.SURG && t.seats.SURG.stats.theatreSteps++;
  if (theatre === 0 && t.seats.ICU) t.seats.ICU.stats.anaesthetics++;
  attend(ctx, p, 'SURG');
  if (p.route?.code === 'X') {
    // Exploratory Polishing: always wrong, risk HIGH, no flip.
    p.route = null;
    p.flags.decree = false;
    emit(ctx, { type: 'POLISHING', table: t.no, seat: p.seat, patient: p.id, severity: 2, tag: 'Authority gradient', text: `${p.name} had Exploratory Polishing. It was the wrong treatment.` });
    deteriorate(ctx, t, p, 'Exploratory Polishing');
    return;
  }
  emit(ctx, { type: 'THEATRE', table: t.no, seat: 'SURG', patient: p.id, text: `${theatre === 1 ? 'Second Theatre' : 'Theatre'}: ${nextStep(p)?.label ?? 'step'} on ${p.name}.` });
  completeStep(ctx, t, p, 'SURG', 'Surgery');
}

// ───────────────────────────── handlers ─────────────────────────────

const ASSESS: H<'ASSESS'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => accessReason(pub, t, role, c.p),
  run: (ctx, t, role, c) => {
    spend(ctx, t, role, 1, c.p, 'ASSESS');
    readLine(ctx, t, patient(ctx.s, c.p), 'ASSESS', role);
  },
};

const OPINION: H<'OPINION'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    const p = pub.patients[c.p]!;
    const roles = effectiveRoles(pub, t, role, p);
    const named = namedOpinions(t, p);
    if (!roles.some((r) => { const k = opinionKind(r); return k && named.includes(k); })) return 'The Compendium does not ask for your opinion on this diagnosis.';
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const roles = effectiveRoles(ctx.s, t, role, p);
    const named = namedOpinions(t, p);
    const k = roles.map(opinionKind).find((x) => x && named.includes(x))!;
    spend(ctx, t, role, 1, c.p, 'OPINION');
    readLine(ctx, t, p, k, role);
  },
};

const SCAN: H<'SCAN'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => accessReason(pub, t, role, c.p) ?? (scannerBox(pub, t, role) < 0 ? 'No scanner slot is free for you this turn.' : null),
  run: (ctx, t, role, c) => {
    spend(ctx, t, role, 1, c.p, 'SCAN');
    useScanner(ctx, t, role);
    readLine(ctx, t, patient(ctx.s, c.p), 'SCAN', role);
  },
};

/** Free Assess: E16 (Emergency), E32 (one per list). */
export function freeAssessSource(pub: PublicState, t: TableState, role: Role): string | null {
  const st = t.seats[role];
  if (!st || st.freeAssessUsedTurn === pub.clock.turn) return null;
  if (role === 'EM' && eventActive(pub, 'E16')) return 'Full Moon';
  if (eventActive(pub, 'E32')) return 'Nurse Educator';
  return null;
}

const FREE_ASSESS: H<'FREE_ASSESS'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: P_TARGET,
  check: (pub, t, role, c) => accessReason(pub, t, role, c.p) ?? (freeAssessSource(pub, t, role) ? null : 'No free Assess this turn.'),
  run: (ctx, t, role, c) => {
    t.seats[role]!.freeAssessUsedTurn = ctx.s.clock.turn;
    spend(ctx, t, role, 0, c.p, 'FREE_ASSESS');
    readLine(ctx, t, patient(ctx.s, c.p), 'ASSESS', role);
  },
};

const SECOND_OPINION: H<'SECOND_OPINION'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    if (!eventActive(pub, 'E22')) return 'Second Opinion is not in play this turn.';
    if (t.seats[role]!.e22UsedTurn === pub.clock.turn) return 'You have used your Second Opinion this turn.';
    if (pub.patients[c.p]!.box !== CONFIRMED) return 'Second Opinion re-checks a confirmed patient.';
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    t.seats[role]!.e22UsedTurn = ctx.s.clock.turn;
    spend(ctx, t, role, 0, c.p, 'SECOND_OPINION');
    const text = `${diagName(p.believedKey).toUpperCase()} — confirmed again. It says the same thing.`;
    reveal(ctx, p, 'ASSESS', text);
    emit(ctx, { type: 'SECOND_OPINION', table: t.no, seat: role, patient: p.id, text: `${p.name}: ${text}`, audience: ['faculty', `seat:${role}`] });
  },
};

function stabiliseReason(pub: PublicState, t: TableState, role: Role, p: Patient, locum: boolean): string | null {
  if (!p.needsStabilise) return 'This patient does not need stabilising.';
  const damp = p.believedKey === 'combustion';
  if (locum) return damp ? 'Only Emergency can Damp a combustion.' : null;
  if (damp && role !== 'EM') return 'Only Emergency can Damp a combustion.';
  const roles = effectiveRoles(pub, t, role, p);
  if (!roles.some((r) => r === 'EM' || r === 'PHYS' || r === 'ICU')) return 'Stabilise is for Emergency, the Physician or the Intensivist.';
  return null;
}

function doStabilise(ctx: Ctx, t: TableState, p: Patient): void {
  p.needsStabilise = false;
  normaliseRoute(p);
  emit(ctx, { type: 'STABILISED', table: t.no, seat: p.seat, patient: p.id, text: `${p.name} stabilised.`, audience: ['faculty', ...(p.seat ? ([`seat:${p.seat}`] as const) : [])] });
}

const STABILISE: H<'STABILISE'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => accessReason(pub, t, role, c.p) ?? stabiliseReason(pub, t, role, pub.patients[c.p]!, false),
  run: (ctx, t, role, c) => {
    spend(ctx, t, role, 1, c.p, 'STABILISE');
    doStabilise(ctx, t, patient(ctx.s, c.p));
  },
};

/** Routes available for a patient right now (by code). */
export function availableRoutes(pub: PublicState, t: TableState, p: Patient): Route[] {
  const out: Route[] = diag(p.believedKey).routes.filter((r) => t.fiveSeat || r.code !== 'P');
  if (p.flags.decree && t.effects.decree === 'COMPLY') out.push(EXPLORATORY_POLISHING);
  return out;
}

const START_ROUTE: H<'START_ROUTE'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    const p = pub.patients[c.p]!;
    if (!availableRoutes(pub, t, p).some((r) => r.code === c.code)) return 'That route is not in the Compendium for this diagnosis.';
    if (p.route && p.route.code === c.code && p.route.diag === p.believedKey) return 'That route is already started.';
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const lost = p.route && p.route.next > 0;
    p.route = { code: c.code, next: 0, startedTurn: ctx.s.clock.turn, diag: c.code === 'X' ? p.believedKey : p.believedKey };
    if (c.code !== 'X') normaliseRoute(p);
    const names: Record<string, string> = { S: 'surgical', M: 'medical', P: 'psych', G: 'standard', X: 'Exploratory Polishing' };
    emit(ctx, {
      type: 'ROUTE_STARTED', table: t.no, seat: role, patient: p.id, text: `Say it aloud: starting ${diagName(p.believedKey)}, ${names[c.code]} route${lost ? ' (steps on the old route are lost)' : ''}.`,
      audience: ['faculty', `seat:${role}`], data: { code: c.code, switched: !!lost },
    });
  },
};

/** Validate the next route step for a TREAT-type action by these roles. */
function treatReason(pub: PublicState, t: TableState, roles: Role[], p: Patient, locum: boolean): string | null {
  if (!p.route) return 'Start a route first.';
  if (p.route.code === 'X') return 'Exploratory Polishing happens in the Theatre.';
  const st = nextStep(p);
  if (!st) return 'The route is complete.';
  if (p.needsStabilise) return 'Stabilise first.';
  if (st.kind === 'THEATRE') return 'The next step is in the Theatre.';
  if (st.kind === 'ICU') return 'The next step is a turn of ICU care: Support the patient in ICU.';
  if (st.kind === 'OBSERVE') return 'The next step is observation: it happens by itself at the end of the turn if the patient is in the right bed.';
  if (st.kind === 'STEP_DOWN') return 'The next step is a step-down by the Intensivist.';
  if (st.kind === 'DISCHARGE') return 'Ready for discharge.';
  if (st.kind === 'STABILISE') return 'Stabilise first.';
  if (locum) {
    if (st.kind === 'SCAN') return 'The Locum cannot run the Scanner step.';
    if (st.inICU) return 'The Locum does not give ICU infusions.';
    if (!st.by.some((b) => b === 'EM' || b === 'PHYS' || b === 'ANY')) return 'The Locum cannot give that.';
  } else if (!stepOwnersOk(st, roles)) {
    return `${st.label} is for ${st.by.map((b) => ({ EM: 'Emergency', SURG: 'the Surgeon', PHYS: 'the Physician', PSYCH: 'the Psychiatrist', ICU: 'the Intensivist', ANY: 'anyone' })[b]).join(' or ')}.`;
  }
  if (st.inICU && !(p.seat === 'ICU' && p.loc.kind === 'bed')) return `${st.label} is given in ICU.`;
  if (st.kind === 'SCAN' && scannerBox(pub, t, roles[0]!) < 0) return 'No scanner slot is free for you this turn.';
  return null;
}

const TREAT: H<'TREAT'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    const p = pub.patients[c.p]!;
    return treatReason(pub, t, effectiveRoles(pub, t, role, p), p, false);
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const st = nextStep(p)!;
    spend(ctx, t, role, 1, c.p, 'TREAT');
    if (st.kind === 'SCAN') {
      useScanner(ctx, t, role);
      readLine(ctx, t, p, 'SCAN', role, { diag: p.route!.diag, code: p.route!.code, next: p.route!.next });
      return;
    }
    completeStep(ctx, t, p, role, st.label);
  },
};

const OPERATE: H<'OPERATE'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    if (role !== 'SURG') return 'Only the Surgeon operates.';
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    const p = pub.patients[c.p]!;
    const closed = theatreClosed(pub);
    if (closed) return closed;
    const th = c.theatre === 1 ? t.board.secondTheatre : t.board.theatre;
    if (!th) return 'The second Theatre is not available this turn.';
    if (th.used) return 'The Theatre has been used this turn.';
    if (c.theatre === 1 && t.board.theatre.booking?.patient === c.p) return 'That patient is already booked in the primary Theatre.';
    if (c.theatre !== 1 && t.board.theatre.booking) return 'The Theatre is already booked this turn.';
    if (t.board.allocations.theatre && t.board.allocations.theatre !== 'SURG') return 'The Lead gave the Theatre to someone else this turn.';
    if (!p.route) return 'Start a route first.';
    if (p.needsStabilise) return 'Stabilise first.';
    const st = p.route.code === 'X' ? EXPLORATORY_POLISHING.steps[0]! : nextStep(p);
    if (!st || st.kind !== 'THEATRE') return 'The next step is not in the Theatre.';
    if (t.effects.decree === 'COMPLY' && p.route.code !== 'X') {
      const pending = Object.values(pub.patients).some((x) => x.table === t.no && x.status === 'ACTIVE' && x.flags.decree);
      if (pending) return 'The Consultant’s decree comes first: every Glowing patient gets Exploratory Polishing before any other operation.';
    }
    if (t.board.vents && ventsFree(pub, t) <= 0) return 'No ventilator is free for the anaesthetic.';
    if (t.board.blood && (st.blood ?? 0) > t.board.blood.units) return `Not enough blood: this needs ${st.blood} units.`;
    return null;
  },
  run: (ctx, t, role, c) => {
    spend(ctx, t, role, 1, c.p, 'OPERATE');
    if (c.theatre === 1) {
      completeTheatre(ctx, t, patient(ctx.s, c.p), 1);
      return;
    }
    t.board.theatre.booking = { patient: c.p, surgeon: role };
    emit(ctx, { type: 'THEATRE_BOOKED', table: t.no, seat: role, patient: c.p, text: `Theatre booked for ${ctx.s.patients[c.p]!.name}; waiting for the anaesthetic.`, audience: ['faculty', 'seat:SURG', 'seat:ICU'] });
    if (t.board.theatre.anaesthetic) completeTheatre(ctx, t, patient(ctx.s, c.p));
  },
};

const ANAESTHETISE: H<'ANAESTHETISE'> = {
  phases: ['ACT'],
  cost: ONE,
  target: (_pub, t) => t.board.theatre.booking?.patient ?? null,
  check: (pub, t, role, c) => {
    if (c.theatre === 1) return 'The second Theatre includes its own anaesthetist.';
    if (role !== 'ICU') return 'Only the Intensivist anaesthetises.';
    const closed = theatreClosed(pub);
    if (closed) return closed;
    if (t.board.theatre.used) return 'The Theatre has been used this turn.';
    if (t.board.theatre.anaesthetic) return 'The anaesthetic is already given this turn.';
    if (t.board.theatre.booking) {
      const p = pub.patients[t.board.theatre.booking.patient];
      if (!p || p.table !== t.no || p.status !== 'ACTIVE') return 'The booked patient is no longer on your table.';
      if (p.needsStabilise) return 'Stabilise first.';
      const st = p.route?.code === 'X' ? EXPLORATORY_POLISHING.steps[0]! : nextStep(p);
      if (!st || st.kind !== 'THEATRE') return 'The booked patient no longer needs Theatre.';
      if (t.board.vents && ventsFree(pub, t) <= 0) return 'No ventilator is free for the anaesthetic.';
      if (t.board.blood && (st.blood ?? 0) > t.board.blood.units) return `Not enough blood: this needs ${st.blood} units.`;
    }
    return null;
  },
  run: (ctx, t, role) => {
    spend(ctx, t, role, 1, t.board.theatre.booking?.patient ?? null, 'ANAESTHETISE');
    t.board.theatre.anaesthetic = role;
    emit(ctx, { type: 'ANAESTHETIC', table: t.no, seat: role, text: t.board.theatre.booking ? 'Anaesthetic given.' : 'Anaesthetist standing by in the Theatre.', audience: ['faculty', 'seat:SURG', 'seat:ICU'] });
    if (t.board.theatre.booking) completeTheatre(ctx, t, patient(ctx.s, t.board.theatre.booking.patient));
  },
};

export function supportsNeeded(pub: PublicState): number {
  return eventActive(pub, 'E4') ? 2 : 1;
}

const SUPPORT: H<'SUPPORT'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    if (role !== 'ICU') return 'Only the Intensivist supports ICU beds.';
    const p = pub.patients[c.p];
    if (!p || p.table !== t.no || p.seat !== 'ICU' || p.loc.kind !== 'bed' || p.status !== 'ACTIVE') return 'Support is for patients in an ICU bed.';
    if ((p.turnScratch.supports ?? 0) >= supportsNeeded(pub)) return 'Already supported this turn.';
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    spend(ctx, t, role, 1, c.p, 'SUPPORT');
    p.turnScratch.supports = (p.turnScratch.supports ?? 0) + 1;
  },
};

const STEP_DOWN: H<'STEP_DOWN'> = {
  phases: ['ACT'],
  cost: ONE,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    if (role !== 'ICU') return 'Only the Intensivist steps patients down.';
    if (eventActive(pub, 'E26')) return 'Comms are down: no step-downs this turn.';
    const p = pub.patients[c.p];
    if (!p || p.table !== t.no || p.seat !== 'ICU' || p.status !== 'ACTIVE') return 'Step-down is for patients in ICU.';
    const st = nextStep(p);
    if (!st || st.kind !== 'STEP_DOWN') return 'This patient is not ready to step down.';
    const to = seatOfBed(st.bed as 'SW' | 'MW');
    if (!t.seats[to]) return `There is no ${to === 'SURG' ? 'Surgeon' : 'Physician'} at this table.`;
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const st = nextStep(p)!;
    const to = seatOfBed(st.bed as 'SW' | 'MW');
    spend(ctx, t, role, 1, c.p, 'STEP_DOWN');
    p.flags.ventHeld = false;
    place(ctx.s, t, to, p, 'auto');
    p.route!.next++;
    p.referral = { from: 'ICU', turn: ctx.s.clock.turn };
    normaliseRoute(p);
    const waited = t.tableStats.stepDownWaiting[p.id];
    if (waited !== undefined) {
      if (ctx.s.clock.turn === waited + 1) t.tableStats.stepDownOnTime++;
      else t.tableStats.stepDownLate++;
      delete t.tableStats.stepDownWaiting[p.id];
    }
    emit(ctx, { type: 'STEPPED_DOWN', table: t.no, seat: to, patient: p.id, severity: 1, text: `${p.name} stepped down from ICU to the ${st.bed === 'SW' ? 'surgical' : 'medical'} ward.`, audience: ['faculty', `seat:${to}`, 'seat:ICU'] });
  },
};

/**
 * @param pub Public state.
 * @param t Acting table.
 * @param role Acting seat.
 * @param patientId Discharge target.
 * @returns Exclusive ledger key and booking source, or null when charged.
 */
function dischargeAllowance(pub: PublicState, t: TableState, role: Role, patientId: PatientId): { list: Role; booking: Booking | null } | null {
  if (eventActive(pub, 'E7')) return t.effects.e7FirstDischargeUsed.includes(role) ? null : { list: role, booking: null };
  const p = pub.patients[patientId];
  if (!p || p.table !== t.no || p.status !== 'ACTIVE' || !p.seat || !['SURG', 'PHYS', 'PSYCH'].includes(p.seat)) return null;
  if (t.timeOut.turn === pub.clock.turn || t.effects.e7FirstDischargeUsed.includes(p.seat)) return null;
  const booking = t.bookings.find((b) => b.kind === 'BED_MEETING' && b.from <= pub.clock.turn && b.until !== null && pub.clock.turn <= b.until);
  return booking ? { list: p.seat, booking } : null;
}

function dischargeReason(pub: PublicState, t: TableState, role: Role | 'LOCUM', p: Patient): string | null {
  if ((p.seat === 'SURG' || p.seat === 'PHYS') && eventActive(pub, 'E2')) return 'Bed Block: no discharges from the wards this turn.';
  if (!p.route) return 'Start a route first.';
  const st = nextStep(p);
  if (!st || st.kind !== 'DISCHARGE' || p.needsStabilise) return 'The route is not complete yet.';
  if (p.turnScratch.warnRoute) return 'Wait until next turn: a risky (⚠) step was done this turn.';
  if ((role === 'LOCUM' || (role === 'EM' && !effectiveRoles(pub, t, role, p).includes('PHYS'))) && p.colour !== 'GREEN') return role === 'EM' ? 'Emergency discharges Greens only. Refer the patient.' : 'The Locum discharges Greens only.';
  return null;
}

/** Discharge resolution shared by seats and the Locum. */
function doDischarge(ctx: Ctx, t: TableState, p: Patient, by: Role | 'LOCUM'): void {
  const back = ctx.s.backs[p.id]!;
  const wasConfirmed = p.box === CONFIRMED && !(back.barnacle && p.believedKey !== back.truthKey);
  const holder = p.seat;
  if (!wasConfirmed) {
    if (back.truthKey !== p.believedKey) {
      applyRiskIfWrong(ctx, t, p, back.truthKey, routeOf(p)?.riskIfWrong ?? 'LOW', 'Discharge');
      return;
    }
    reveal(ctx, p, 'TRUTH', `TRUTH: ${diagName(back.truthKey)}`);
    if (back.bounceBack) {
      bounce(ctx, t, p, 'discharged without confirmation');
      return;
    }
  }
  release(ctx, t, p);
  p.status = 'DISCHARGED';
  t.dashboard.saved++;
  if (holder) t.seats[holder]!.stats.discharges++;
  if (by !== 'LOCUM' && p.flags.special === 'vip') t.seats[by]!.stats.vipCompleted = true;
  emit(ctx, { type: 'DISCHARGED', table: t.no, seat: holder, patient: p.id, severity: 0, text: `${p.name} discharged.`, audience: ['faculty', ...(holder ? ([`seat:${holder}`] as const) : [])] });
}

/** Put a patient in the Bounce-back slot (returns two turns later, one colour worse). */
export function bounce(ctx: Ctx, t: TableState, p: Patient, why: string): void {
  const holder = p.seat ?? p.flags.lastSeat ?? null;
  release(ctx, t, p);
  p.status = 'BOUNCE_PENDING';
  t.bounceSlot.push({ id: p.id, returnTurn: ctx.s.clock.turn + 2 });
  const back = ctx.s.backs[p.id];
  if (back?.trap && !t.trapList.includes(p.id)) t.trapList.push(p.id);
  emit(ctx, { type: 'BOUNCE_PENDING', table: t.no, seat: holder, patient: p.id, severity: 2, tag: 'Premature closure; flow', text: `${p.name} went home and will come back in two turns (${why}).` });
}

const DISCHARGE: H<'DISCHARGE'> = {
  phases: ['ACT'],
  cost: (pub, t, role, c) => dischargeAllowance(pub, t, role, c.p) ? 0 : 1,
  target: P_TARGET,
  check: (pub, t, role, c) => accessReason(pub, t, role, c.p) ?? dischargeReason(pub, t, role, pub.patients[c.p]!),
  run: (ctx, t, role, c) => {
    const allowance = dischargeAllowance(ctx.s, t, role, c.p);
    const cost = allowance ? 0 : 1;
    if (allowance) {
      t.effects.e7FirstDischargeUsed.push(allowance.list);
      if (allowance.booking) allowance.booking.used++;
    }
    spend(ctx, t, role, cost, c.p, 'DISCHARGE');
    doDischarge(ctx, t, patient(ctx.s, c.p), role);
  },
};

const EARLY_DISCHARGE: H<'EARLY_DISCHARGE'> = {
  phases: ['ACT'],
  cost: (pub, t, role, c) => dischargeAllowance(pub, t, role, c.p) ? 0 : 1,
  target: P_TARGET,
  check: (pub, t, role, c) => {
    const a = accessReason(pub, t, role, c.p);
    if (a) return a;
    const p = pub.patients[c.p]!;
    if ((p.seat === 'SURG' || p.seat === 'PHYS') && eventActive(pub, 'E2')) return 'Bed Block: no discharges from the wards this turn.';
    if (p.colour === 'RED') return 'Reds cannot go home early.';
    if (p.seat === 'ICU') return 'Not from ICU.';
    if (p.needsStabilise) return 'Stabilise first.';
    if (p.turnScratch.warnRoute) return 'Wait until next turn: a risky (⚠) step was done this turn.';
    const obs = onlyObservesRemain(p);
    if (obs <= 0) return obs === 0 ? 'Nothing to skip: discharge normally.' : 'Treatment steps remain.';
    return null;
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const allowance = dischargeAllowance(ctx.s, t, role, c.p);
    const cost = allowance ? 0 : 1;
    if (allowance) {
      t.effects.e7FirstDischargeUsed.push(allowance.list);
      if (allowance.booking) allowance.booking.used++;
    }
    spend(ctx, t, role, cost, c.p, 'EARLY_DISCHARGE');
    const skipped = onlyObservesRemain(p);
    p.turnScratch.earlySkipped = skipped;
    p.turnScratch.earlyBy = role;
    release(ctx, t, p);
    p.status = 'DISCHARGED';
    emit(ctx, { type: 'EARLY_DISCHARGE', table: t.no, seat: role, patient: p.id, severity: 1, tag: 'Flow', text: `${p.name} sent home early, skipping ${skipped} observe${skipped > 1 ? 's' : ''}. Read the EARLY line at the end of the turn.`, audience: ['faculty', `seat:${role}`] });
  },
};

export function locumPresent(pub: PublicState, t: TableState): { idx: number; actionsLeft: number } | null {
  const turn = pub.clock.turn;
  let best: { idx: number; actionsLeft: number } | null = null;
  t.locums.forEach((l, idx) => {
    if (l.arrivesTurn <= turn && (l.untilTurn === null || l.untilTurn >= turn) && l.actionsLeft > 0 && !best) best = { idx, actionsLeft: l.actionsLeft };
  });
  return best;
}

const USE_LOCUM: H<'USE_LOCUM'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    const p = pub.patients[c.p];
    if (!p || p.table !== t.no || p.seat !== role || p.status !== 'ACTIVE') return 'The Locum works on patients on your own list.';
    if (!locumPresent(pub, t)) return 'There is no Locum with actions left.';
    switch (c.action) {
      case 'ASSESS':
        return null;
      case 'STABILISE':
        return stabiliseReason(pub, t, role, p, true);
      case 'TREAT':
        return treatReason(pub, t, [role], p, true);
      case 'DISCHARGE':
        return dischargeReason(pub, t, 'LOCUM', p);
    }
  },
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const loc = locumPresent(ctx.s, t)!;
    t.locums[loc.idx]!.actionsLeft--;
    const booking = t.locums[loc.idx]!.booking;
    if (booking !== undefined) t.bookings[booking]!.used++;
    attend(ctx, p, 'LOCUM');
    emit(ctx, { type: 'LOCUM_USED', table: t.no, seat: role, patient: p.id, text: `The Locum: ${c.action.toLowerCase()} on ${p.name}.`, data: { action: c.action } });
    switch (c.action) {
      case 'ASSESS':
        readLine(ctx, t, p, 'ASSESS', 'LOCUM');
        break;
      case 'STABILISE':
        doStabilise(ctx, t, p);
        break;
      case 'TREAT':
        completeStep(ctx, t, p, 'LOCUM', nextStep(p)?.label ?? 'Treatment');
        break;
      case 'DISCHARGE':
        doDischarge(ctx, t, p, 'LOCUM');
        break;
    }
  },
};

export const ACT_HANDLERS = {
  ASSESS, OPINION, SCAN, FREE_ASSESS, SECOND_OPINION, STABILISE, START_ROUTE, TREAT, OPERATE, ANAESTHETISE, SUPPORT,
  STEP_DOWN, DISCHARGE, EARLY_DISCHARGE, USE_LOCUM,
};

/** Is the patient waiting for ICU (next step ICU, not in ICU)? */
export function waitingICU(p: Patient): boolean {
  const st = nextStep(p);
  return !!st && st.kind === 'ICU' && p.seat !== 'ICU';
}

export { commsDown };
